function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function serializeForm(form) {
  const obj = {};
  const formData = new FormData(form);
  const properties = {};

  for (let key of formData.keys()) {
    const value = formData.get(key);

    if ((match = key.match(/properties\[(.*)\]/))) {
      properties[match.pop()] = value;
      obj["properties"] = properties;
    } else {
      obj[key] = value;
    }
  }
  return JSON.stringify(obj);
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

function setWindowHeight() {
  document.body.style.setProperty("--vh", window.innerHeight + "px");
}

function observeStickyness(stickyElement, ghostClass = "") {
  window.stickyObserver ||= new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      !entry.isIntersecting
        ? entry.target.stickyElement.classList.add("stuck")
        : entry.target.stickyElement.classList.remove("stuck");
    });
  });

  if (stickyElement.stickyGhost == undefined) {
    stickyElement.stickyGhost = document.createElement("div");
    stickyElement.stickyGhost.classList.add(
      "sticky-ghost",
      `${ghostClass}-sticky-ghost`
    );
    stickyElement.stickyGhost.stickyElement = stickyElement;
    stickyElement.before(stickyElement.stickyGhost);
  }

  window.stickyObserver.observe(stickyElement.stickyGhost);
}

window.addEventListener("resize", () => {
  if (this.resizeTimeout) clearTimeout(this.timer);
  this.resizeTimeout = setTimeout(setWindowHeight, window.durations.short);
});
setWindowHeight();

//
// Header Bar

class HeaderBar extends HTMLElement {
  constructor() {
    super();

    this.header = this.closest("header");
    this.header.addEventListener("mouseenter", this.mouseEnter.bind(this));
    this.header.addEventListener("mouseleave", this.mouseLeave.bind(this));

    observeStickyness(this.header, "header-bar-sticky-ghost");

    this.mobileDrawerDialogOpen = false;

    this.search = this.querySelector("header-search");
    this.dropdowns = this.querySelectorAll("header-dropdown");
  }

  mouseEnter(ev) {
    this.activate();
  }

  mouseLeave(ev) {
    this.deactivate();
  }

  activate() {
    this.header.classList.add("active");
  }

  deactivate() {
    if (!this.mobileDrawerDialogOpen) {
      this.header.classList.remove("active");
    }
  }
}

customElements.define("header-bar", HeaderBar);

class HeaderDropdown extends HTMLElement {
  constructor() {
    super();

    this.handle = this.dataset.handle;
    this.dialog = this.querySelector("dialog");
    this.backdrop = this.querySelector(".dialog-backdrop");
    this.links = document.querySelectorAll(
      `[data-header-dropdown=${this.handle}]`
    );
    this.openers = [...this.links, this.dialog];
    this.closers = [this.backdrop];

    this.openers.forEach((el) => {
      if (window.isTouch) {
        el.addEventListener("click", this.openerClickedOnTouch.bind(this));
      } else {
        el.addEventListener("mouseenter", this.openerMouseEnter.bind(this));
        el.addEventListener(
          "mouseleave",
          this.openerMouseLeaveDelayed.bind(this)
        );
      }
    });

    this.closers.forEach((el) => {
      el.addEventListener(
        "mouseenter",
        this.openerMouseLeaveDelayed.bind(this)
      );
    });
  }

  openerClickedOnTouch(ev) {
    ev.preventDefault();
    this.toggle();
  }

  openerMouseEnter(ev) {
    clearTimeout(this.leaveTimer);
    this.activate();
  }

  openerMouseLeaveDelayed(ev) {
    this.leaveTimer = setTimeout(
      this.deactivate.bind(this),
      window.durations.short
    );
  }

  activate() {
    if (!window.breakpoints.desktop.matches) return;
    if (this.open) return;

    this.open = true;
    this.dialog.show();
    this.links.forEach((link) => link.classList.add("active"));

    this.search?.deactivate();
  }

  deactivate() {
    if (!this.open) return;

    this.open = false;
    this.dialog.close();
    this.links.forEach((link) => link.classList.remove("active"));
  }

  toggle() {
    this.open ? this.deactivate() : this.activate();
  }
}

customElements.define("header-dropdown", HeaderDropdown);

//
// Search Bar
//
class HeaderSearch extends HTMLElement {
  constructor() {
    super();

    this.header = this.closest("header");
    this.headerBar = this.closest("header-bar");

    this.searchButton = this.querySelector(".header__search__button");
    this.searchBar = this.querySelector(".header__search__bar");
    this.searchInput = this.searchBar.querySelector("input");

    this.dropdownDialog = this.header.querySelector(".search-dropdown-dialog");
    this.dropdownDialogBackdrop =
      this.dropdownDialog.querySelector(".dialog-backdrop");

    this.searchResults = this.dropdownDialog.querySelector(
      ".header__search-results"
    );

    this.setupMobile();
    this.setupInput();
    this.setupDialog();

    this.debouncedSearch = debounce(
      this.search.bind(this),
      window.durations.short
    );
  }

  setupMobile() {
    this.mobileSearchBar = this.searchBar.cloneNode(true);
    this.mobileSearchBar.hidden = false;
    this.mobileSearchInput = this.mobileSearchBar.querySelector("input");
    this.dropdownDialog.prepend(this.mobileSearchBar);

    this.mobileSearchInput.addEventListener("input", (ev) => {
      this.searchInput.value = ev.target.value;
      this.searchInput.dispatchEvent(new Event("input"));
    });
  }

  setupDialog() {
    this.searchButton.addEventListener(
      "click",
      this.onSearchButtonClick.bind(this)
    );

    if (window.isTouch) return;

    [this.dropdownDialog].forEach((el) => {
      el.addEventListener("mouseenter", this.openerMouseEnter.bind(this));
      el.addEventListener(
        "mouseleave",
        this.openerMouseLeaveDelayed.bind(this)
      );
    });

    [this.dropdownDialogBackdrop].forEach((el) => {
      el.addEventListener(
        "mouseenter",
        this.openerMouseLeaveDelayed.bind(this)
      );
    });
  }

  setupInput() {
    this.searchInput.addEventListener("blur", this.onSearchBlur.bind(this));
    this.searchInput.addEventListener("focus", this.onSearchFocus.bind(this));
    this.searchInput.addEventListener(
      "input",
      this.onSearchInputKeypress.bind(this)
    );
  }

  onSearchButtonClick(ev) {
    ev.preventDefault();
    this.toggle();
  }

  onSearchBlur(ev) {
    this.deactivateTimer = setTimeout(
      this.deactivate.bind(this),
      window.durations.long
    );
  }

  onSearchFocus(ev) {
    clearTimeout(this.deactivateTimer);
  }

  onSearchInputKeypress(ev) {
    if (this.searchInput.value.length >= 1) {
      this.debouncedSearch(this.searchInput.value);
    } else {
      this.showBlankSlate();
    }

    if (!this.mobileSearchInput) return;
    this.mobileSearchInput.value = this.searchInput.value;
  }

  openerClickedOnTouch(ev) {
    ev.preventDefault();
    this.toggle();
  }

  openerMouseEnter(ev) {
    clearTimeout(this.leaveTimer);
    this.activate();
  }

  openerMouseLeaveDelayed(ev) {
    this.leaveTimer = setTimeout(
      this.deactivate.bind(this),
      window.durations.short
    );
  }

  showBlankSlate() {
    if (!window.breakpoints.desktop.matches) return;
    this.deactivateDropdown();
  }

  showResults() {
    this.activateDropdown();
  }

  activate() {
    this.searchButton.hidden = true;
    this.searchBar.hidden = false;
    this.searchInput.focus();
    this.headerBar.activate();

    if (
      window.breakpoints.desktop.matches &&
      this.searchInput.value.length == 0
    )
      return;
    this.activateDropdown();
  }

  deactivate() {
    this.deactivateDropdown();
    this.searchButton.hidden = false;
    this.searchBar.hidden = true;
  }

  toggle() {
    this.searchBar.hidden ? this.activate() : this.deactivate();
  }

  activateDropdown() {
    if (this.dropdownDialogOpen) return;

    this.dropdownDialogOpen = true;
    this.dropdownDialog.show();
    this.searchInput.focus();
  }

  deactivateDropdown() {
    if (!this.dropdownDialogOpen) return;

    this.dropdownDialogOpen = false;
    this.dropdownDialog.close();
  }

  toggleDropdown() {
    this.dropdownDialogOpen
      ? this.deactivateDropdown()
      : this.activateDropdown();
  }

  search(searchValue) {
    const searchURL = new URL(location.href);
    searchURL.pathname = window.routes.predictive_search_url;
    searchURL.searchParams.set("q", searchValue);
    searchURL.searchParams.set("resources[type]", "product");
    searchURL.searchParams.set(
      "resources[options][unavailable_products]",
      "hide"
    );
    searchURL.searchParams.set(
      "resources[options][fields]",
      "title,product_type,variants.title"
    );
    searchURL.searchParams.set("resources[limit]", 9);
    searchURL.searchParams.set("section_id", "search-dropdown");

    fetch(searchURL)
      .then((response) => response.text())
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector(".search-dropdown__results").innerHTML;
        this.searchResults.innerHTML = resultsMarkup;
        this.showResults();
      })
      .catch((error) => {
        console.error(error);
      });
  }
}

customElements.define("header-search", HeaderSearch);

//
// Notification Bar
//

class NotificationBar extends HTMLElement {
  constructor() {
    super();

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        entry.isIntersecting ? this.onAppearing() : this.onDisappearing();
      });
    });
    this.intersectionObserver.observe(this.querySelector("span"));
    document.body.classList.add("with-notification-bar");
  }

  onAppearing() {
    document.body.classList.add("notification-bar--visible");
  }

  onDisappearing() {
    document.body.classList.remove("notification-bar--visible");
  }
}

customElements.define("notification-bar", NotificationBar);

//
// Mobile Drawer
//
class MobileDrawer extends HTMLElement {
  constructor() {
    super();

    this.header = this.closest("header");
    this.headerBar = this.header.querySelector("header-bar");

    this.dialog = this.closest("dialog");
    this.dialog.addEventListener("close", this.close.bind(this));
    this.menus = this.querySelectorAll("mobile-drawer-menu");

    this.openers = document.querySelectorAll("a[href='#mobile-drawer']");
    this.openers.forEach((opener) => {
      opener.addEventListener("click", this.onOpenerClick.bind(this));
    });

    window.breakpoints.desktop.addEventListener("change", (query) => {
      if (query.matches && this.dialog.open) {
        this.close();
      }
    });
  }

  onOpenerClick(ev) {
    ev.preventDefault();

    this.toggle();
    document.body.classList.toggle("fixed");
  }

  open() {
    this.dialog.show();
    this.headerBar.mobileDrawerDialogOpen = true;
    this.headerBar.activate();
    this.openers.forEach((opener) => {
      opener.classList.add("active");
    });
  }

  close() {
    this.dialog.close();
    this.headerBar.mobileDrawerDialogOpen = false;
    this.headerBar.deactivate();
    this.openers.forEach((opener) => {
      opener.classList.remove("active");
    });
    this.menus.forEach((menu) => {
      menu.close();
    });
  }

  toggle() {
    this.dialog.open ? this.close() : this.open();
  }
}

customElements.define("mobile-drawer", MobileDrawer);

class MobileDrawerMenu extends HTMLElement {
  constructor() {
    super();

    this.headerBar = this.closest("header").querySelector("header-bar");
    this.drawer = this.closest("mobile-drawer");
    this.openLink = this.querySelector(":scope > a");
    this.subMenu = this.querySelector(":scope > div");
    this.backButton = this.subMenu.querySelector("a.has-icon");

    this.openLink.addEventListener("click", this.onOpenLinkClick.bind(this));
    this.backButton.addEventListener(
      "click",
      this.onBackButtonClick.bind(this)
    );
  }

  onOpenLinkClick(ev) {
    ev.preventDefault();
    this.open();
  }

  onBackButtonClick(ev) {
    ev.preventDefault();
    this.close();
  }

  open() {
    this.classList.add("active");
  }

  close() {
    this.classList.remove("active");
  }
}

customElements.define("mobile-drawer-menu", MobileDrawerMenu);

//
// Dialog Stuff
//
document.addEventListener("click", (ev) => {
  if (ev.target.nodeName == "DIALOG") {
    const dialogCloser = ev.target.querySelector("dialog-closer");
    if (!dialogCloser) return;

    dialogCloser.click();
  }
});

class DialogToggler extends HTMLElement {
  constructor() {
    super();

    this.dialog =
      document.querySelector(this.dataset.dialog || this.dataset.modal) ||
      this.closest("dialog");
    this.addEventListener("click", this.onClick);

    if (this.dialog.open) {
      this.activate();
    }
  }

  onClick(ev) {
    ev.preventDefault();
    this.toggle();
  }

  open() {
    if (this.dataset.modal) {
      document.body.classList.add("fixed");
      this.dialog.showModal();
    } else {
      this.dialog.show();
    }
    this.activate();
  }

  close() {
    document.body.classList.remove("fixed");

    this.dialog.close();
    this.deactivate();
  }

  toggle() {
    if (this.dialog.open) {
      this.close();
    } else {
      this.open();
    }
  }

  all() {
    return Array.from(document.querySelectorAll("dialog-toggler")).filter(
      (el) => el.dialog == this.dialog
    );
  }

  activate() {
    this.all().forEach((el) => el.classList.add("active"));
  }

  deactivate() {
    this.all().forEach((el) => el.classList.remove("active"));
  }
}

customElements.define("dialog-toggler", DialogToggler);

class DialogOpener extends DialogToggler {
  constructor() {
    super();
  }

  onClick(ev) {
    ev.preventDefault();
    this.open();
  }
}

customElements.define("dialog-opener", DialogOpener);

class DialogCloser extends DialogToggler {
  constructor() {
    super();
  }

  onClick(ev) {
    ev.preventDefault();
    this.close();
  }
}

customElements.define("dialog-closer", DialogCloser);

//
// Quantity Input
//
class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });

    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  onButtonClick(ev) {
    ev.preventDefault();

    const previousValue = this.input.value;
    ev.target.name === "plus" ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);
  }
}

customElements.define("quantity-input", QuantityInput);

//
// RadioSelect
//

const RadioSelectCheckedEvent = new Event("radio:checked", {
  bubbles: true,
});

class RadioSelect extends HTMLElement {
  constructor() {
    super();

    this.details = this.querySelector("details");
    this.label = this.querySelector("var");
    this.placeholder = this.querySelector(".radio-select__placeholder");
    this.checked = this.querySelector(":checked");
    this.radios = this.querySelectorAll('input[type=radio]');
    this.default = this.querySelector("[data-checked]");
    this.buttons = document.querySelectorAll(this.dataset.controls);

    this.addEventListener("change", this.onRadioButtonChange.bind(this));
    this.addEventListener(
      "radio:checked",
      this.onRadioButtonChecked.bind(this)
    );

    if (!this.checked) {
      this.setSelectionState(false);
    }
  }

  setSelectionState(selected) {
    this.classList.toggle("not-selected", !selected);
    this.radios.forEach(radio => radio.required = selected);
    this.buttons.forEach(button => button.disabled = !selected);
  }

  onRadioButtonChecked(ev) {
    let secondaryInput = ev.target.nextElementSibling;
    if (secondaryInput.nodeName == "INPUT") secondaryInput.checked = true;

    this.default?.removeAttribute("data-checked");
    this.setSelectionState(true);
  }

  onRadioButtonChange(ev) {
    this.onRadioButtonChecked(ev);

    this.dataset.previousValue = this.dataset.selectedValue;
    this.dataset.selectedValue = ev.target.value;

    this.close();
    this.setLabel();
  }

  setLabel() {
    if (!this.label) return;
    const checkedRadio = this.querySelector(":checked");
    this.label.innerText = decodeURI(
      checkedRadio.dataset.label || checkedRadio.value
    );
    this.setSelectionState(true);
  }

  open() {
    if (!this.details) return;
    this.details.setAttribute("open", "open");
  }

  close() {
    if (!this.details) return;
    this.details.removeAttribute("open");
  }
}

customElements.define("radio-select", RadioSelect);

//
// Scroll Sticker
//
class ScrollSticker extends HTMLElement {
  constructor() {
    super();

    this.container =
      this.closest("[data-scroll-sticker]") || this.parentElement;
    this.io = new IntersectionObserver(this.onInteresect.bind(this), {
      threshold: 0,
    });
    this.io.observe(this.container);

    this.setValues();
  }

  addScrollListener() {
    this.scrollListener = this.onScroll.bind(this);
    window.addEventListener("scroll", this.scrollListener);
  }

  removeScrollListener() {
    window.removeEventListener("scroll", this.scrollListener);
  }

  onScroll() {
    this.setValues();
  }

  setValues() {
    let positionOfContainer = this.container.getBoundingClientRect().y;
    let heightOfContainer = this.container.getBoundingClientRect().height;
    let halfHeightOfContainer = heightOfContainer / 2;
    let positionFromTop = window.innerHeight - positionOfContainer;
    let positionFromBottom = positionFromTop - halfHeightOfContainer;
    let percentageFromHeight = (100 / window.innerHeight) * positionFromBottom;

    this.parentElement.style.setProperty(
      "--scroll-sticker-top",
      percentageFromHeight + "%"
    );
    this.style.setProperty(
      "--scroll-sticker-height",
      this.getBoundingClientRect().height + "px"
    );
  }

  onInteresect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.addScrollListener();
      } else {
        this.removeScrollListener();
      }
    });
  }
}

customElements.define("scroll-sticker", ScrollSticker);

//
// BottomSticker
//
class BottomSticker extends HTMLElement {
  constructor() {
    super();

    this.resizeObserver = new ResizeObserver((entries) => {
      entries.forEach(this.calculateMargin.bind(this));
    });
    this.resizeObserver.observe(this);
  }

  calculateMargin() {
    this.margin = 0;
    let previousSibling = this.previousElementSibling;
    while (previousSibling) {
      this.margin += previousSibling.getBoundingClientRect().height;
      previousSibling = previousSibling.previousElementSibling;
    }

    this.style.setProperty("--calculated-margin", this.margin + "px");
  }
}

customElements.define("bottom-sticker", BottomSticker);

//
// External Content
//
class ExternalContent extends HTMLElement {
  constructor() {
    super();

    this.url = this.dataset.url;
    this.selector = this.dataset.selector;

    if (!this.url || !this.selector) return;

    this.fetchContent();
  }

  fetchContent() {
    fetch(this.url)
      .then((response) => response.text())
      .then((text) => {
        let element = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector(this.selector);

        if (!element) return;

        this.append(element);
      });
  }
}

customElements.define("external-content", ExternalContent);

//
// Better Image
//
class BetterImage extends HTMLElement {
  constructor() {
    super();

    this.img = this.querySelector("img");
    this.srcset = this.img.getAttribute("srcset");
  }

  // This is a workaround for Safari browsers <15.4 as they mess-up 'object-fit' for dynamically added images
  reset() {
    this.img.setAttribute("srcset", "");
    this.img.setAttribute("srcset", this.srcset);
  }

  load() {
    if (this.img.dataset.src) this.img.src = this.img.dataset.src;
    if (this.img.dataset.srcset) this.img.srcset = this.img.dataset.srcset;
    this.img.hidden = false;
  }

  show() {
    this.load();
    this.hidden = false;
  }
}

customElements.define("better-image", BetterImage);
